/**
 *
 * @param array Array to index
 * @param key key to index the array
 * @param mapper function to map the values to index
 * @returns returns an object with the key as the index and the value as the value, if mapper is provided, the value will be the result of the mapper function
 */
export function index<T extends Record<string, any>, R = T>(
  array: T[],
  key: (keyof T & string) | ((item: T) => string),
  mapper?: (item: T) => R
): Record<string, R> {
  return array.reduce(
    (obj, item) => {
      let k
      if (typeof key === 'function') {
        k = key(item)
      } else {
        k = item[key]
      }
      if (k === undefined) return obj

      obj[k] = mapper ? mapper(item) : (item as unknown as R)
      return obj
    },
    {} as Record<string, R>
  )
}

/**
 *
 * @param array Array to index
 * @param key key to index the array
 * @param mapper function to map the values to index
 * @returns returns an object with the key as the index and an array of values as the value, if mapper is provided, the values will be the result of the mapper function
 */
export function indexList<T extends Record<string, any>, R = T>(
  array: T[],
  key: keyof T & string,
  mapper?: (item: T) => R
): Record<string, R[]> {
  return array.reduce(
    (obj, item) => {
      const k = item[key]
      if (k === undefined) {
        throw new Error(`Key ${String(key)} not found in array`)
      }
      if (!obj[k]) obj[k] = []
      obj[k].push(mapper ? mapper(item) : (item as unknown as R))
      return obj
    },
    {} as Record<string, R[]>
  )
}

export function toChunks<T>(array: T[], chunkSize: number): T[][] {
  const chunks: T[][] = []
  for (let i = 0, j = array.length; i < j; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize))
  }
  return chunks
}

export function unique<K extends string, T extends { [key in K]: any }>(
  items: T[],
  key: K = 'id' as K
): T[] {
  const seen = new Set<string>()
  return items.filter(item => {
    if (seen.has(item[key])) return false
    seen.add(item[key])
    return true
  })
}

/**
 * Merge two arrays of objects based on a key
 * @param arr1 Array of objects
 * @param arr2 Array of objects
 * @param key Key shared by both arrays
 * @returns An array of objects with the merged values
 */
export function merge<
  K extends PropertyKey,
  V extends PropertyKey,
  T1 extends Record<K, V>,
  T2 extends Record<K, V>
>(arr1: T1[], arr2: T2[], key: K): (T1 & T2)[] {
  const map = new Map<V, T1 & Partial<T2>>()

  arr1.forEach(item => map.set(item[key], item as T1 & Partial<T2>))
  arr2.forEach(item => {
    const existing = map.get(item[key])
    if (existing) {
      map.set(item[key], { ...existing, ...item })
    } else {
      map.set(item[key], item as unknown as T1 & Partial<T2>)
    }
  })

  return Array.from(map.values()) as (T1 & T2)[]
}

/**
 *
 * @param array Array to split
 * @param condition Condition to split the array
 * @returns return an array with two arrays, the first one with the elements that meet the condition and the second one with the elements that do not meet the condition
 */
export function split<T>(array: T[], condition: (item: T) => boolean): [T[], T[]] {
  return array.reduce(
    (acc, item) => {
      if (condition(item)) {
        acc[0].push(item)
      } else {
        acc[1].push(item)
      }
      return acc
    },
    [[], []] as [T[], T[]]
  )
}

export function sum(array: number[]): number {
  return array.reduce((acc, curr) => acc + curr, 0)
}

export function zip<T, U>(arr1: T[], arr2: U[]): Array<[T, U]> {
  const length = Math.min(arr1.length, arr2.length)
  const result: Array<[T, U]> = []
  for (let i = 0; i < length; i++) {
    result.push([arr1[i], arr2[i]])
  }
  return result
}

export function count<K extends string, T extends { [key in K]: any }>(
  array: T[],
  key: K
): Record<string, number> {
  return array.reduce(
    (acc, item) => {
      const k = item[key]
      if (k === undefined) return acc
      acc[k] = (acc[k] || 0) + 1
      return acc
    },
    {} as Record<string, number>
  )
}
